import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RowActions from "@/components/RowActions";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Building2, ChevronRight } from "lucide-react";
import BrainTypeField from "@/components/BrainTypeField";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  description: "",
  location: "",
  admin_name: "",
  admin_email: "",
  admin_password: "",
  admin_brain_type: null,
};

export default function SuperAdminInstitutes() {
  const navigate = useNavigate();
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institutes");
      setInstitutes(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setOpen(true);
  };

  const openEdit = (i) => {
    setEditing(i);
    setForm({
      name: i.name,
      description: i.description || "",
      location: i.location || "",
      admin_name: i.admin_name,
      admin_email: i.admin_email,
      admin_password: "",
      admin_brain_type: i.admin_brain_type ?? null,
    });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          description: form.description,
          location: form.location,
          admin_name: form.admin_name,
          admin_email: form.admin_email,
        };
        if (form.admin_password) payload.admin_password = form.admin_password;
        await api.put(`/institutes/${editing.id}`, payload);
        toast.success("Institute updated");
      } else {
        await api.post("/institutes", form);
        toast.success("Institute created");
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/institutes/${confirmTarget.id}`);
      toast.success("Institute deleted");
      setConfirmTarget(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-12 max-w-7xl" data-testid="super-admin-institutes-page">
      <PageHeader
        overline="Super Admin"
        title="Institutes"
        description="Provision new institutes and their primary administrators across the platform."
        action={
          <Button
            onClick={openCreate}
            data-testid="create-institute-button"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
          >
            <Plus strokeWidth={1.5} size={18} className="mr-2" />
            New Institute
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5E2DC] sm:max-w-lg" data-testid="institute-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold">
              {editing ? "Edit Institute" : "Create Institute"}
            </DialogTitle>
            <DialogDescription className="text-[#5C635A]">
              {editing
                ? "Update institute details and admin profile."
                : "This will provision the institute and create its admin login."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <Field
              label="Institute Name"
              testid="institute-name-input"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                Description
              </Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E]"
                data-testid="institute-description-input"
              />
            </div>
            <Field
              label="Location"
              testid="institute-location-input"
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              placeholder="City, country — visible to prospects during registration"
            />
            <div className="h-px bg-[#E5E2DC] my-4" />
            <Field
              label="Admin Name"
              testid="institute-admin-name-input"
              value={form.admin_name}
              onChange={(v) => setForm({ ...form, admin_name: v })}
              required
            />
            <Field
              label="Admin Email"
              type="email"
              testid="institute-admin-email-input"
              value={form.admin_email}
              onChange={(v) => setForm({ ...form, admin_email: v })}
              required
            />
            <Field
              label={editing ? "New Password (optional)" : "Admin Password"}
              type="password"
              testid="institute-admin-password-input"
              value={form.admin_password}
              onChange={(v) => setForm({ ...form, admin_password: v })}
              required={!editing}
              placeholder={editing ? "Leave blank to keep current" : ""}
            />
            <BrainTypeField
              value={form.admin_brain_type}
              onChange={(v) => setForm({ ...form, admin_brain_type: v })}
              testid="institute-admin-brain-type-input"
              label="Admin Brain Type (1–9, optional)"
            />
            {error && (
              <p
                className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                data-testid="institute-error"
              >
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="institute-submit-button"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                {saving
                  ? editing
                    ? "Saving..."
                    : "Creating..."
                  : editing
                  ? "Save changes"
                  : "Create Institute"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
        title="Delete this institute?"
        description={
          confirmTarget
            ? `This will permanently delete "${confirmTarget.name}", its admin, all coaches, all clients, and all onboarding responses associated with it. This action cannot be undone.`
            : ""
        }
        onConfirm={handleDelete}
        loading={deleting}
        testIdPrefix="institute-delete"
      />

      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="institutes-list">
        <div className="grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold border-b border-[#E5E2DC]">
          <div className="col-span-4">Institute</div>
          <div className="col-span-3">Admin</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1 text-right">Created</div>
          <div className="col-span-1"></div>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-[#8A9188]">Loading institutes…</div>
        ) : institutes.length === 0 ? (
          <div className="px-6 py-16 text-center" data-testid="institutes-empty">
            <Building2 strokeWidth={1.5} className="mx-auto text-[#B37A5F] mb-3" size={32} />
            <p className="text-[#5C635A]">No institutes yet. Create the first one.</p>
          </div>
        ) : (
          institutes.map((i) => (
            <div
              key={i.id}
              onClick={() => navigate(`/details/institute/${i.id}`)}
              className="grid grid-cols-12 px-6 py-5 border-b last:border-0 border-[#E5E2DC] items-center hover:bg-[#F9F8F6] transition-colors cursor-pointer group"
              data-testid={`institute-row-${i.id}`}
            >
              <div className="col-span-4">
                <p className="font-medium text-[#1A1D1A] flex items-center gap-2">
                  {i.name}
                  <ChevronRight strokeWidth={1.5} size={14} className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
                {i.description && (
                  <p className="text-xs text-[#8A9188] mt-1 line-clamp-1">{i.description}</p>
                )}
              </div>
              <div className="col-span-3 text-sm text-[#5C635A]">{i.admin_name}</div>
              <div className="col-span-3 text-sm text-[#5C635A] truncate">{i.admin_email}</div>
              <div className="col-span-1 text-right text-xs text-[#8A9188] uppercase tracking-wider">
                {new Date(i.created_at).toLocaleDateString()}
              </div>
              <div className="col-span-1 flex justify-end">
                <RowActions
                  onEdit={() => openEdit(i)}
                  onDelete={() => setConfirmTarget(i)}
                  testIdPrefix={`institute-${i.id}`}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
